
import React, { useState, useEffect, useCallback } from 'react';
import { useStore, StoreTypes } from 'context';
import { DeviceType } from 'constants/chimeTypes'
import styles from './index.module.scss';
import { Roles } from 'constants/role';
import ChatRoom from 'components/ChatRoom';
import classnames from 'classnames';
import SelectComponent from 'components/SelectComponent'
import VolumeMeter from 'components/Tools/VolumeMeter'
import Icon from 'components/Icon';
import { useChimeError } from 'customHooks/chimeError';
import {
    RemoteVideo,
    LocalVideo,
    useVideoInputs,
    useSelectVideoInputDevice,
    useAudioInputs,
    useAudioOutputs,
    useLocalVideo,
    useToggleLocalMute,
    useLocalAudioOutput,
    useMeetingManager,
    useRemoteVideoTileState,
    useSelectVideoQuality,
    useAudioVideo,
    Notification,
    Severity,
    useRosterState,
    useNotificationState,
    useNotificationDispatch,
    NotificationProvider,
    NotificationGroup,
    ActionType,
    SignalStrength
} from 'amazon-chime-sdk-component-library-react';

export const TeacherVideo = React.memo(({ isFull }) => {
    const meetingManager = useMeetingManager();
    const meetingId = meetingManager.meetingId;
    const [attendeeId, setAttendeeId] = useState('');
    const [signalStrength, setSignalStrength] = useState('');
    const { attendeeIdToTileId } = useRemoteVideoTileState();
    const audioVideo = useAudioVideo();

    const bindAttendeeCallback = (audioVideo, attendeeId) => {
        audioVideo.realtimeSubscribeToVolumeIndicator(
            attendeeId,
            (attendeeId, volume, muted, signalStrength, externalUserId) => {
                if (signalStrength === null) {
                    // signalStrength has not changed
                    return;
                }

                setSignalStrength(signalStrength);
            }
        );
    }

    useEffect(() => {
        if (!meetingId || attendeeId) return;
        const getTeacherAttendeeId = async () => {
            const response =
                await fetch(`${process.env.REACT_APP_CHIME_API_URL}/getTeacher?meetingId=${meetingId}`);
            const { data } = await response.json();
            if (data) {
                setAttendeeId(data.AttendeeId)
            }
        }

        getTeacherAttendeeId();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meetingId, attendeeIdToTileId])


    useEffect(() => {
        if (!audioVideo || !attendeeId) return;

        bindAttendeeCallback(audioVideo, attendeeId);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendeeId, audioVideo])



    return attendeeId && attendeeIdToTileId[attendeeId] ?
        <>
            <RemoteVideo tileId={attendeeIdToTileId[attendeeId]} />
            {
                !isFull && <VolumeMeter className={styles.volumeMeter} width={50} height={15} attendeeId={attendeeId} />
            }
            <SignalStrength className={classnames(styles.signal, GetSignalStrengthCssName(signalStrength))} />
        </>
        : null
})

const GetSignalStrengthCssName = (signalStrength) => {
    if (signalStrength == 1)
        return styles.signalHigh;
    else if (signalStrength == 0)
        return styles.signalLow;
    else
        return styles.signalMedium
};

const LiveStreamViewerPanel = ({ role }) => {
    const [{ rewardInfo, isFullVideo }] = useStore(StoreTypes.course);
    const { fireworks, good, like, trophy } = rewardInfo;

    const meetingManager = useMeetingManager();
    const selectVideoQuality = useSelectVideoQuality();
    const audioVideo = useAudioVideo();

    const { isVideoEnabled, toggleVideo } = useLocalVideo();
    const { devices: videoInputDevices } = useVideoInputs();
    const selectVideoInput = useSelectVideoInputDevice();

    const { devices: audioInputDevices } = useAudioInputs();
    const { devices: audioOutputDevices } = useAudioOutputs();

    const { muted, toggleMute } = useToggleLocalMute();
    const { isAudioOn, toggleAudio } = useLocalAudioOutput();

    // const { roster } = useRosterState();
    // const attendees = Object.values(roster);

    const selfAttendeeId = meetingManager.configuration.credentials.attendeeId;
    const { processDeviceError, showNotification } = useChimeError();
    const { notifications } = useNotificationState();
    const dispatch = useNotificationDispatch();

    const notificationItems =
        notifications.map(({ id, ...rest }) => (
            <Notification
                key={id}
                {...rest}
                onClose={() => dispatch({ type: ActionType.REMOVE, payload: id })}
            />
        ));

    useEffect(() => {
        let audioError = false;
        let videoError = false;
        if (audioInputDevices.length == 0 || audioInputDevices.some(item => item.label.length == 0))
            audioError = true;
        else if (videoInputDevices.length == 0 || videoInputDevices.some(item => item.label.length == 0))
            videoError = true;

        if (videoError) {
            processDeviceError(`攝影機`, "PermissionDeniedError");
        }
        if (audioError) {
            processDeviceError(`麥克風`, "PermissionDeniedError");
        }

    }, [audioInputDevices, videoInputDevices])


    useEffect(() => {
        const attendeeChangedObserver = async (presentAttendeeId, present, externalUserId) => {
            let roleType;
            if (externalUserId)
                roleType = externalUserId.split("$")[1];
            if (roleType == Roles.TUTOR) {
                if (present)
                    showNotification(Severity.INFO, "對方已上線")
                else
                    showNotification(Severity.WARNING, "對方已離線", true)
            }

        };
        if (audioVideo) {
            //  audioVideo.addObserver(myObserver);
            audioVideo.realtimeSubscribeToAttendeeIdPresence(attendeeChangedObserver);
        }
    }, [audioVideo]);



    useEffect(() => {
        (async () => {
            if (!isVideoEnabled) {
                try {
                    await toggleVideo();
                }
                catch (error) {
                    processDeviceError(`攝影機`, error);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        isFullVideo ? selectVideoQuality('720p') : audioVideo.chooseVideoInputQuality(320, 240, 10, 300);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFullVideo])

    const formatDeviceValue = useCallback(devices => devices.map(item => (
        { label: item.label, value: item.deviceId }
    )
    ), [])


    const onVideoInputChange = async (deviceId) => {
        if (!audioVideo || !meetingManager)
            return;
        //先關閉再開啟
        try {
            await audioVideo.chooseVideoInputDevice(null);
            await audioVideo.chooseVideoInputDevice(deviceId);
            audioVideo.startLocalVideoTile();
        }
        catch (error) {
            processDeviceError(`攝影機`, error);
        }
    }

    const onAudioInputChange = async (deviceId) => {
        await meetingManager.selectAudioInputDevice(deviceId).catch((error) => {
            processDeviceError(`麥克風`, error);
        });
    }

    return (
        <div className={classnames(styles.discussionPanel, styles.wrapper)}>
            <div id="content" className={styles.chatContent}>
                <div className={styles.toolsControls}>
                    {
                        videoInputDevices.length > 0 &&
                        <div className={styles.localVideoSelect}>
                            <div onClick={toggleVideo}>
                                {
                                    isVideoEnabled ?
                                        <Icon name="OpenVideo" /> :
                                        <Icon name="CloseVideo" />
                                }
                            </div>
                            {
                                videoInputDevices.some(item => item.label.length == 0) ?
                                    <span>請檢查攝影機權限</span>
                                    :
                                    <SelectComponent
                                        type={DeviceType.VideoDevice}
                                        optionsList={formatDeviceValue(videoInputDevices)}
                                        defaultSelectText={videoInputDevices[0].label}
                                        onChange={onVideoInputChange}
                                    />
                            }
                        </div>
                    }
                    {
                        audioInputDevices.length > 0 &&
                        <div className={styles.localSound}>
                            <div onClick={toggleMute}>
                                {
                                    muted ?
                                        <Icon name="CloseAudioInput" /> :
                                        <Icon name="OpenAudioInput" />
                                }
                            </div>
                            {
                                audioInputDevices.some(item => item.label.length == 0) ?
                                    <span>請檢查麥克風權限</span>
                                    :
                                    <SelectComponent
                                        type={DeviceType.AudioInputDevice}
                                        optionsList={formatDeviceValue(audioInputDevices)}
                                        defaultSelectText={audioInputDevices[0] && audioInputDevices[0].label}
                                        onChange={onAudioInputChange}
                                    />}
                        </div>
                    }
                    {
                        audioOutputDevices.length > 0 &&
                        <div className={styles.localAudio}>
                            <div onClick={toggleAudio}>
                                {
                                    isAudioOn ?
                                        <Icon name="OpenAudioOutput" /> :
                                        <Icon name="CloseAudioOutput" />
                                }
                            </div>
                            <SelectComponent
                                type={DeviceType.AudioOutputDevice}
                                optionsList={formatDeviceValue(audioOutputDevices)}
                                defaultSelectText={audioOutputDevices[0] && audioOutputDevices[0].label}
                                onChange={meetingManager.selectAudioOutputDevice}
                            />
                        </div>
                    }
                </div>
                <div className={classnames(styles.localVideo, { [styles.localVideoHide]: isFullVideo, [styles.localVideoBG]: isFullVideo })}>
                    {
                        !isFullVideo && <TeacherVideo isFull={isFullVideo} />
                    }
                    <div className={classnames(styles.fullVideoMessage, { [styles.fullVideoMessageHide]: !isFullVideo })} >
                        <Icon name="StudentVideoIsFull" />
                    </div>
                </div>

                <div className={styles.localVideo}>
                    <LocalVideo />
                    <VolumeMeter className={styles.volumeMeter} width={50} height={15} attendeeId={selfAttendeeId} />
                </div>
                {/* <div className={styles.remoteVideoContainer}>
                                {teacherId && <RemoteVideoGroup isContentShareEnabled={false} viewMode="Room" />}
                            </div> */}
                {
                    (role === Roles.TUTOR_USER || role === Roles.ADMIN) &&
                    <div className={styles.emojiCounts}>
                        <div className={styles.emojiCount}>
                            <div><Icon name="FireworksIcon" /></div>
                            <div className={styles.countText}>{fireworks && fireworks}</div>
                        </div>
                        <div className={styles.emojiCount}>
                            <div><Icon name="GoodIcon" /></div>
                            <div className={styles.countText}>{good && good}</div>
                        </div>
                        <div className={styles.emojiCount}>
                            <div><Icon name="LikeIcon" /></div>
                            <div className={styles.countText}>{like && like}</div>
                        </div>
                        <div className={styles.emojiCount}>
                            <div><Icon name="TrophyIcon" /></div>
                            <div className={styles.countText}>{trophy && trophy}</div>
                        </div>
                    </div>
                }
                <div className={classnames(styles.chatRoomWrapper, { [styles.tutoruser]: role === Roles.TUTOR_USER || role === Roles.ADMIN })}>
                    <ChatRoom />
                </div>
                <div >
                    <NotificationGroup>
                        {notificationItems}
                    </NotificationGroup>
                </div>
            </div>
        </div>
    )
}



export default LiveStreamViewerPanel;
